import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import AccountDisplay from '../../components/account/account-display/account-display.component';
import AddNote from '../../components/notes/add-note/add-note.component';
import Button from '../../components/reusable/button/button.component';
import ComponentModal from '../../components/reusable/component-modal/component-modal.component';
import NoteDisplay from '../../components/notes/note-display/note-display.component';
import OrdersTable from '../../components/reusable/tables/orders-table/orders-table.component';
import Spinner from '../../components/reusable/spinner/spinner.component';
import UpdateAccount from '../../components/account/update-account/update-account.component';
import UpdateNote from '../../components/notes/update-note/update-note.component';
import NoteCard from '../../components/notes/note-card/note-card.component';

import { ToastContext } from '../../contexts/toast.context';

import Client from '../../tools/client';

import {
    MainContainer,
    SectionContainer
} from './account.styles';

import {
    BackLink,
    Subtitle,
    MainTitle
} from '../../styles/page.styles';

const client = new Client();

const AccountPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const { id } = useParams();
    const [account, setAccount] = useState(null);
    const [showUpdateAccount, setShowUpdateAccount] = useState(false);

    const [showAddNoteModal, setShowAddNoteModal] = useState(false);
    const [selectedNote, setSelectedNote] = useState(null);
    const [showNoteDisplayModal, setShowNoteDisplayModal] = useState(false);
    const [showNoteEditModal, setShowNoteEditModal] = useState(false);

    const { successToast, errorToast } = useContext(ToastContext);

    useEffect(() => {
        getAccount();
    }, []);

    const getAccount = async () => {
        setLoading(true);
        const res = await client.getAccountById(id);
        setAccount(res);
        setLoading(false);
    };

    const onRefresh = async () => {
        const res = await client.getAccountById(id);
        setAccount(res);

        const refreshedNote = res.UserNotes?.find(
            (entry) => entry.Note.id === selectedNote?.id
        )?.Note;

        if (refreshedNote) setSelectedNote({ ...refreshedNote });
    };

    const updateAccount = async (params) => {
        const data = {
            ...params,
            id: account.id
        };

        const res = await client.updateAccount(data);

        if (res.length > 0) {
            await getAccount();
            setShowUpdateAccount(false);
            successToast('Account Updated');
        } else {
            errorToast('There was an error updating account. Please try again.');
        }
    };

    return (
        <MainContainer>
            <BackLink onClick={() => navigate(`/accounts`)}>
                Back to Users
            </BackLink>
            {loading ? (
                <Spinner />
            ) : account.length === 0 ? (
                <MainTitle>User Does Not Exist</MainTitle>
            ) : showUpdateAccount ? (
                <UpdateAccount account={account} submit={updateAccount} showUpdate={setShowUpdateAccount} />
            ) : (
                <SectionContainer>
                    <AccountDisplay account={account} getAccount={getAccount} showUpdate={setShowUpdateAccount} />
                    <OrdersTable orders={account.Orders} />

                    <MainTitle>User Notes</MainTitle>
                    <Button onClick={() => setShowAddNoteModal(true)}>Add Note</Button>

                    {account.UserNotes?.length > 0 ? (
                        account.UserNotes.map((entry, idx) => (
                            <NoteCard
                                key={idx}
                                note={entry.Note}
                                createdAt={entry.createdAt}
                                onClick={() => {
                                    setSelectedNote(entry.Note);
                                    setShowNoteDisplayModal(true);
                                }}
                            />
                        ))
                    ) : (
                        <Subtitle>No notes yet.</Subtitle>
                    )}

                    <ComponentModal isOpen={showAddNoteModal} onClose={() => setShowAddNoteModal(false)} title="Add Note">
                        <AddNote
                            onSubmit={async (noteData) => {
                                noteData.append('userId', account.id );
                                await client.createUserNote(noteData);
                                await getAccount();
                                setShowAddNoteModal(false);
                            }}
                        />
                    </ComponentModal>

                    <ComponentModal isOpen={showNoteDisplayModal} onClose={() => setShowNoteDisplayModal(false)} title="User Note">
                        <NoteDisplay
                            note={selectedNote}
                            setShowEdit={() => {
                                setShowNoteDisplayModal(false);
                                setShowNoteEditModal(true);
                            }}
                            onRefresh={onRefresh}
                        />
                    </ComponentModal>

                    <ComponentModal isOpen={showNoteEditModal} onClose={() => setShowNoteEditModal(false)} title="Edit Note">
                        <UpdateNote
                            note={selectedNote}
                            onUpdate={async (noteId, updatedData) => {
                                await client.updateNote(noteId, updatedData);
                                await getAccount();
                                setShowNoteEditModal(false);
                            }}
                            onDelete={async (noteId) => {
                                await client.deleteUserNote(account.id, noteId);
                                await getAccount();
                                setShowNoteEditModal(false);
                            }}
                            onClose={() => setShowNoteEditModal(false)}
                        />
                    </ComponentModal>
                </SectionContainer>
            )}
        </MainContainer>
    );
};

export default AccountPage;